import { inject, Injectable } from '@angular/core';
import { ExportFileType } from '../../model/enum/export-file-type';
import { HttpClient, HttpParams } from '@angular/common/http';
import { SnackbarService } from '../../shared/services/snackbar.service';
import { environment } from '@orderna/admin-panel/src/environments/environment';
import { Subject } from 'rxjs';

// TODO: Refactor this service and its naming.
//
// `ExportExcelPdfService` supports more than Excel/PDF exports, including
// `print` and `txt`, so the name doesn't reflects its current responsibilities and
// does not scale well as additional export/output types are introduced.
//
// The service is also no longer analytics-specific after the addition of
// `exportCompleteSubject`, yet `ROUTE_PREFIX` is still hard-coded to an
// analytics route. This coupling is misleading and should be removed or made
// configurable as part of the refactor.
@Injectable({
  providedIn: 'root',
})
export class ExportExcelPdfService {
  private readonly ROUTE_PREFIX = `${environment.api}/analytics/export`;
  private http = inject(HttpClient);
  private snackbarService = inject(SnackbarService);

  // Subject to notify when export completes
  private exportCompleteSubject = new Subject<{
    type: 'print' | 'pdf' | 'excel' | 'txt';
    success: boolean;
  }>();

  // Observable for components to subscribe to
  exportComplete$ = this.exportCompleteSubject.asObservable();

  export(options: { endpoint: string; params: HttpParams; print: boolean }) {
    const { endpoint, params, print } = options;

    // Append file type to the URL path (pdf/excel).
    // ⚠️ Note: This may later change to using a query parameter instead.
    const formatParam = params.get('format') as ExportFileType;
    const formatUrlSegment = formatParam === ExportFileType.PDF ? 'pdf' : 'excel';
    const exportType = print ? 'print' : (formatParam === ExportFileType.PDF ? 'pdf' : 'excel');

    // Remove format
    const normalizedParams = params.delete('format');

    return this.http
      .post(
        `${this.ROUTE_PREFIX}/${endpoint}/${formatUrlSegment}`,
        {},
        { params: normalizedParams, responseType: 'blob', observe: 'response' }
      )
      .subscribe({
        next: (res) => {
          this.downloadOrPrintFile(res, formatParam, print);
          // Notify that export completed successfully
          this.exportCompleteSubject.next({ type: exportType, success: true });
        },
        error: (error) => {
          console.error(error);
          this.snackbarService.showError(
            'Something went wrong while exporting. Please try again later.'
          );
          // Notify that export failed
          this.exportCompleteSubject.next({ type: exportType, success: false });
        },
      });
  }

  downloadOrPrintFile(fileBlob: any, fileType: string, print: boolean) {
    const fileName = fileBlob.headers.get('x-filename');
    const url = window.URL.createObjectURL(fileBlob.body);

    if (fileType === 'pdf') {
      if (print) {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = url;
        document.body.appendChild(iframe);

        iframe.onload = () => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          // Clean up iframe after print dialog opens
          setTimeout(() => {
            document.body.removeChild(iframe);
            window.URL.revokeObjectURL(url);
          }, 1000);
        };
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  }
}
